import type { ChatMention } from "@xop/dashboard-shared";

/**
 * The two pure @-mention helpers for the chat composer, extracted from
 * `ChatComposer.tsx` so the caret-detection + candidate-ranking logic
 * unit-tests directly.
 */

/** Find the active `@query` immediately before the caret: an `@` at the start
 * or after whitespace, followed by non-space chars up to the caret. Returns the
 * `@` index + the query text, or null when the caret isn't inside a mention
 * token. Names contain spaces, so the query filters on the contiguous
 * first-token only (the standard @-picker behaviour). */
export function detectMention(
  text: string,
  caret: number,
): { at: number; query: string } | null {
  for (let i = caret - 1; i >= 0; i--) {
    const ch = text[i];
    if (ch === "@") {
      const before = text[i - 1];
      if (i === 0 || before === " " || before === "\n" || before === "\t") {
        return { at: i, query: text.slice(i + 1, caret) };
      }
      return null;
    }
    if (ch === " " || ch === "\n" || ch === "\t") return null;
  }
  return null;
}

/** Rank teammates for a mention query: prefix matches before substring matches,
 * case-insensitive; an empty query lists everyone. Capped to a short list. */
export function filterMentionCandidates(
  candidates: readonly ChatMention[],
  query: string,
): ChatMention[] {
  const q = query.trim().toLowerCase();
  return candidates
    .map((c) => {
      const name = c.display_name.toLowerCase();
      const rank = !q ? 0 : name.startsWith(q) ? 0 : name.includes(q) ? 1 : -1;
      return { c, rank };
    })
    .filter((s) => s.rank >= 0)
    .sort((a, b) => a.rank - b.rank)
    .slice(0, 6)
    .map((s) => s.c);
}
